import React from 'react';
import { defineMessages } from 'react-intl-next';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';


import { getExtensionManifest } from '@atlassian/company-hub-common';
import type { CustomSitesExtensionProviderProps } from '@atlassian/company-hub-common';

import { LoadableAfterPaint } from '@confluence/loadable';

import { CAROUSEL_VERSION_1, CAROUSEL_VERSION_2 } from './carouselVersion';
import { carouselExtensionKey, carouselExtensionType } from './carouselConstants';
import { Layout } from './carouselTypes';
import type { CarouselParameters } from './carouselTypes';
import {
	createNewFrameWithDefaultParameters,
	carouselNewParametersV2,
	FRAME_V2_DEFAULT_IMAGE_SRC,
} from './CarouselConfigPanel/useCarouselExtensionParametersAndActions';

const CarouselExtension = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CarouselExtension" */ './CarouselExtension'))
			.CarouselExtension,
});

const slideImages = [
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615716272018-7c5b1216e262?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615715757448-f8e0cdccf1e2?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615716038858-2f9a60c330a6?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
];

const i18n = defineMessages({
	carouselMacroTitle: {
		id: 'custom-sites-extensions.carousel.manifest.macro-title',
		defaultMessage: 'Carousel',
		description: 'Title of carousel macro to be displayed in toolbar/shortcut macros dropdown.',
	},
	carouselMacroDescription: {
		id: 'custom-sites-extensions.carousel.manifest.macro-description',
		defaultMessage: 'Create a rotating trio of dynamic frames.',
		description:
			'Description of carousel macro to be displayed in toolbar/shortcut macros dropdown',
	},
	carouselMacroTitleNew: {
		id: 'custom-sites-extensions.carousel.manifest.macro-title.new',
		defaultMessage: 'Carousel [New]',
		description:
			'Title of carousel macro to be displayed in toolbar/shortcut macros dropdown. The [New] text is a text label indicating that this feature is new.',
	},
});

const getV1Frames = () => {
	return slideImages.map(({ imageSrc, imageAltText }) =>
		createNewFrameWithDefaultParameters(false, { imageSrc, imageAltText }),
	);
};

const carouselFrames = [
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: Layout.SPLIT_IMAGE_LEFT,
	},
	// TODO update default with the new layout type once available
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: Layout.NO_IMAGE_BACKGROUND,
	},
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: Layout.SPLIT_IMAGE_RIGHT,
	},
];
const getV2Frames = () => {
	return carouselFrames.map(({ imageSrc, layout }) =>
		createNewFrameWithDefaultParameters(true, { imageSrc, layout }),
	);
};

export const carouselParameters = (extensionTitle: string, isCarouselV2Enabled = false) =>
	isCarouselV2Enabled
		? {
				cards: getV2Frames(),
				extensionTitle,
				version: CAROUSEL_VERSION_2,
				...carouselNewParametersV2,
			}
		: {
				cards: getV1Frames(),
				extensionTitle,
				version: CAROUSEL_VERSION_1,
			};

export const carouselExtensionProvider = async ({
	contentId,
	editorActions,
	intl,
	openConfigPanel,
	setUpsellModalSource,
}: CustomSitesExtensionProviderProps) => {
	const title = intl.formatMessage(i18n.carouselMacroTitleNew);
	const type = carouselExtensionType;
	const description = intl.formatMessage(i18n.carouselMacroDescription);
	const render =
		async () =>
		({ node }) => {
			return <CarouselExtension extensionNode={node} contentId={contentId} />;
		};

	const icons = {
		'48': () =>
			import(
				/* webpackChunkName: "loadable-CarouselMacroIcon" */
				'@confluence/icons/entry-points/CarouselMacroIcon'
			).then((mod) => mod.CarouselMacroIcon),
	};

	const parameters = carouselParameters(
		intl.formatMessage(i18n.carouselMacroTitle),
		fg('company_hub_carousel_v2'),
	) as CarouselParameters;

	const update = async () => {
		const selectedNode = editorActions?.getSelectedNode()?.toJSON();
		const selectedLocalId = selectedNode?.attrs?.localId;

		if (!!selectedLocalId && openConfigPanel) {
			openConfigPanel(selectedLocalId);
		}
	};

	return new DefaultExtensionProvider([
		getExtensionManifest<CarouselParameters>({
			key: carouselExtensionKey,
			icons,
			parameters,
			update,
			render,
			title,
			type,
			description,
			setUpsellModalSource,
			source: 'premiumCarouselInsert',
		}),
	]);
};
